<template>
  <v-card class="d-flex flex-column h-100">
    <v-card-title
      class="d-flex align-center justify-space-between pt-4 pb-2 px-4 flex-shrink-0"
    >
      <h2 class="text-h6 font-weight-semibold">
        {{ t(isUpdate ? strings.update_title : strings.create_title) }}
      </h2>
      <v-btn
        icon="mdi-close"
        variant="text"
        density="comfortable"
        @click="onCancel"
      />
    </v-card-title>

    <v-form
      ref="formRef"
      v-model="valid"
      @submit.prevent="onSubmit"
      class="d-flex flex-column overflow-hidden flex-grow-1"
    >
      <v-tabs v-model="activeTab" class="flex-shrink-0">
        <v-tab value="trace">Trace</v-tab>
        <v-tab value="condition">Condition</v-tab>
        <v-tab value="listing">Listing</v-tab>
        <v-tab v-if="part?.id" value="files"> Files </v-tab>
      </v-tabs>

      <v-window v-model="activeTab" class="overflow-y-auto flex-grow-1">
        <!-- TRACE: manufacturer, part type, turbine model, frame, casting #, part #, serial #, description -->
        <v-window-item value="trace" class="pa-4" eager>
          <v-select
            v-model="manufacturer"
            :label="t(strings.manufacturerLabel)"
            :placeholder="t(strings.manufacturerPlaceholder)"
            v-bind="commonInputProps"
            :rules="[requiredRule]"
            :items="turbineBladeManufacturers"
          />

          <v-select
            v-model="partType"
            :label="t(strings.partTypeLabel)"
            :items="partTypeEnum"
            v-bind="commonInputProps"
            clearable
          />

          <v-text-field
            v-model="turbineModel"
            :label="t(strings.turbineModelLabel)"
            :placeholder="t(strings.turbineModelPlaceholder)"
            v-bind="commonInputProps"
            :rules="[requiredRule]"
          />

          <v-text-field
            v-model="frame"
            :label="t(strings.frameLabel)"
            :placeholder="t(strings.framePlaceholder)"
            v-bind="commonInputProps"
            :rules="[requiredRule]"
          />

          <v-text-field
            v-model="castingNumber"
            :label="t(strings.castingNumberLabel)"
            :placeholder="t(strings.castingNumberPlaceholder)"
            v-bind="commonInputProps"
          />

          <v-text-field
            v-model="partNumber"
            :label="t(strings.partNumberLabel)"
            :placeholder="t(strings.partNumberPlaceholder)"
            v-bind="commonInputProps"
            :rules="[requiredRule]"
          />

          <v-text-field
            v-model="serialNumber"
            :label="t(strings.serialNumberLabel)"
            :placeholder="t(strings.serialNumberPlaceholder)"
            v-bind="commonInputProps"
            hint="This field is hidden in the public view."
          />

          <v-textarea
            v-model="description"
            :label="t(strings.descriptionLabel)"
            :placeholder="t(strings.descriptionPlaceholder)"
            v-bind="commonInputProps"
          />
        </v-window-item>

        <!-- CONDITION: condition, date listed, date sold, tags, address, upgrades, hours/starts/refurb/trips -->
        <v-window-item value="condition" class="pa-4" eager>
          <v-select
            v-model="condition"
            :label="t(strings.conditionLabel)"
            :items="[
              { title: 'New', value: 'new' },
              { title: 'Repairable', value: 'repairable' },
              { title: 'Repaired', value: 'repaired' },
              { title: 'As Removed', value: 'as removed' },
              { title: 'Inspected', value: 'inspected' },
              { title: 'Scrap', value: 'scrap' },
            ]"
            v-bind="commonInputProps"
            :rules="[requiredRule]"
          />

          <v-text-field
            v-if="!creating"
            v-model="dateListed"
            :label="t(strings.dateListedLabel)"
            v-bind="commonInputProps"
            type="date"
          />

          <v-text-field
            v-if="!creating"
            v-model="dateSold"
            :label="t(strings.dateSoldLabel)"
            v-bind="commonInputProps"
            type="date"
          />

          <v-combobox
            v-if="!creating"
            v-model="tags"
            :label="t(strings.tagsLabel)"
            multiple
            chips
            closable-chips
            v-bind="commonInputProps"
          />

          <div v-if="!creating" class="mb-4">
            <h3 class="mb-2">{{ t(strings.addressLabel) }}</h3>
            <p class="text-body-2 text-medium-emphasis">
              The address will be hidden from public view; for internal use
              only.
            </p>

            <AddressForm v-model="address" :disabled="false" />
          </div>

          <v-text-field
            v-if="!creating"
            v-model="upgradeDesignations"
            :label="t(strings.upgradeDesignationsLabel)"
            :placeholder="t(strings.upgradeDesignationsPlaceholder)"
            v-bind="commonInputProps"
          />

          <v-text-field
            v-model.number="totalFactoredFiredHours"
            :label="t(strings.totalFactoredFiredHoursLabel)"
            :placeholder="t(strings.totalFactoredFiredHoursPlaceholder)"
            v-bind="commonInputProps"
            type="number"
          />

          <v-text-field
            v-model.number="totalFactoredFiredStarts"
            :label="t(strings.totalFactoredFiredStartsLabel)"
            :placeholder="t(strings.totalFactoredFiredStartsPlaceholder)"
            v-bind="commonInputProps"
            type="number"
          />

          <v-text-field
            v-model.number="timesRefurbished"
            :label="t(strings.timesRefurbishedLabel)"
            :placeholder="t(strings.timesRefurbishedPlaceholder)"
            v-bind="commonInputProps"
            type="number"
          />

          <v-text-field
            v-model.number="emergencyTrips"
            :label="t(strings.emergencyTripsLabel)"
            :placeholder="t(strings.emergencyTripsPlaceholder)"
            v-bind="commonInputProps"
            type="number"
          />
        </v-window-item>

        <!-- LISTING: status, quantity, matched hardware, thumbnail, org -->
        <v-window-item value="listing" class="pa-4" eager>
          <v-select
            v-if="!creating"
            v-model="status"
            :label="t(strings.statusLabel)"
            :items="['available', 'pending', 'sold', 'removed']"
            v-bind="commonInputProps"
          />

          <v-text-field
            v-model.number="quantity"
            :label="t(strings.quantityLabel)"
            :placeholder="t(strings.quantityPlaceholder)"
            v-bind="commonInputProps"
            type="number"
            :rules="[requiredRule]"
          />

          <v-text-field
            v-model="priceDollars"
            :label="t(strings.priceUnitAmountLabel)"
            :placeholder="t(strings.priceUnitAmountPlaceholder)"
            prefix="$"
            v-bind="commonInputProps"
            :rules="[priceRule]"
            inputmode="decimal"
            autocomplete="off"
          />

          <v-checkbox
            v-model="matchedHardware"
            :label="t(strings.matchedHardwareLabel)"
            color="primary"
            v-bind="commonInputProps"
          />

          <v-select
            v-if="!creating"
            v-model="thumbnailUrl"
            :label="t(strings.thumbnailUrlLabel)"
            :items="photoFileOptions"
            :placeholder="t(strings.thumbnailUrlPlaceholder)"
            :no-data-text="t(strings.noThumbnailUrlPlaceholder)"
            v-bind="commonInputProps"
            clearable
            item-title="title"
            item-value="value"
          >
            <template v-slot:item="{ props, item }">
              <v-list-item v-bind="props">
                <template v-slot:prepend>
                  <v-img
                    v-if="thumbnailItemUrl(item)"
                    :src="thumbnailItemUrl(item)"
                    :alt="thumbnailItemTitle(item)"
                    width="40"
                    height="40"
                    class="rounded mr-3"
                    cover
                    @error="() => {}"
                  >
                    <template v-slot:error>
                      <v-icon color="grey-lighten-1" size="small"
                        >mdi-image-off</v-icon
                      >
                    </template>
                  </v-img>
                </template>
                <v-list-item-title>{{
                  thumbnailItemTitle(item)
                }}</v-list-item-title>
              </v-list-item>
            </template>
            <template v-slot:selection="{ item }">
              <div class="d-flex align-center">
                <v-img
                  v-if="thumbnailItemUrl(item)"
                  :src="thumbnailItemUrl(item)"
                  :alt="thumbnailItemTitle(item)"
                  width="24"
                  height="24"
                  class="rounded mr-2"
                  cover
                  @error="() => {}"
                >
                  <template v-slot:error>
                    <v-icon color="grey-lighten-1" size="small"
                      >mdi-image-off</v-icon
                    >
                  </template>
                </v-img>
                <span>{{ thumbnailItemTitle(item) }}</span>
              </div>
            </template>
          </v-select>

          <v-select
            v-if="showOrganizationSelect"
            v-model="orgId"
            :items="organizationSelectItems"
            :label="t(strings.organizationLabel)"
            :loading="organizationsQuery.isPending.value"
            item-title="title"
            item-value="value"
            clearable
            v-bind="commonInputProps"
          />
        </v-window-item>

        <v-window-item v-if="part?.id" value="files" class="pa-4">
          <PartFilesSection
            :part-id="part?.id || ''"
            :existing-files="existingFiles"
            :files-loading="filesLoading"
            @file-uploaded="onFileUploaded"
            @file-deleted="onFileDeleted"
          />
        </v-window-item>
      </v-window>

      <div class="pa-4 flex-shrink-0 border-t">
        <v-btn
          block
          variant="flat"
          type="submit"
          color="primary"
          size="large"
          class="text-none font-weight-semibold"
          rounded="lg"
          :loading="loading"
          :disabled="!valid"
        >
          {{ part?.id ? t(strings.saveChanges) : t(strings.addPart) }}
        </v-btn>
      </div>
    </v-form>
  </v-card>
</template>

<script setup lang="ts">
import { part_form_strings as strings } from "./PartForm.strings.ts";
import { useReverseT } from "../../i18n.ts";
import { useCreatePart } from "../../requests/parts/create.ts";
import { useUpdatePart } from "../../requests/parts/update.ts";
import { useGetPartFiles } from "../../requests/parts/index.ts";
import { useListOrganizations } from "../../requests/organizations/index.ts";
import PartFilesSection from "./PartFilesSection.vue";
import { AddressForm } from "@saflib/sdk/components";
import type { Part, PartRequest, PartFile, User } from "@vendata/power-up-spec";
import type { Address } from "@saflib/openapi";
import { ref, computed, watch } from "vue";
import { turbineBladeManufacturers } from "./manufacturers.ts";
import { showError } from "@saflib/vue";
import { getTanstackErrorMessage, TanstackError } from "@saflib/sdk";
import { partTypeEnum, type PartType } from "@vendata/power-up-spec";

const requiredRule = (v: any) => {
  if (v) return true;

  return "Required";
};

const priceRule = (v: string | undefined) => {
  if (v == null || String(v).trim() === "") return true;
  const n = Number.parseFloat(String(v).trim());
  if (Number.isNaN(n)) return "Enter a valid price";
  if (n < 0) return "Price cannot be negative";
  return true;
};

function priceDollarsToCents(input: string): number | null {
  const trimmed = input.trim();
  if (trimmed === "") return null;
  const n = Number.parseFloat(trimmed);
  if (Number.isNaN(n)) return null;
  return Math.round(n * 100);
}

/** VSelect passes the list row object in `item` slots; when the model value is not in `items`, the selection slot receives the primitive value (Vuetify list-items `transformIn`). */
function thumbnailItemUrl(item: unknown): string | undefined {
  if (typeof item === "string") return item;
  if (item && typeof item === "object") {
    const o = item as { raw?: { value?: string }; value?: string };
    return o.raw?.value ?? o.value;
  }
  return undefined;
}

function thumbnailItemTitle(item: unknown): string {
  if (typeof item === "string") return item;
  if (item && typeof item === "object") {
    const o = item as { raw?: { title?: string }; title?: string };
    return o.raw?.title ?? o.title ?? "";
  }
  return "";
}

function formatCreatorLabel(user: User | undefined): string {
  if (!user) return "Unknown creator";
  const n = user.name;
  const full = n ? `${n.first} ${n.last}`.trim() : "";
  if (full) return full;
  return user.email;
}

const { t } = useReverseT();
const commonInputProps = {
  class: "mb-4",
};

// Props
const props = defineProps<{
  part?: Part;
}>();

// Form state
const formRef = ref();
const valid = ref(false);
const activeTab = ref("trace");

// Form data refs
const manufacturer = ref<string>(props.part?.manufacturer || "");
const frame = ref<string>(props.part?.frame || "");
const partNumber = ref<string>(props.part?.part_number || "");
const turbineModel = ref<string>(props.part?.turbine_model || "");
const quantity = ref<number | undefined>(props.part?.quantity);
const priceDollars = ref<string>(
  props.part?.price_unit_amount != null
    ? (props.part.price_unit_amount / 100).toFixed(2)
    : "",
);
const description = ref<string | undefined>(
  props.part?.description ?? undefined,
);
const serialNumber = ref<string | undefined>(
  props.part?.serial_number ?? undefined,
);
const upgradeDesignations = ref<string | undefined>(
  props.part?.upgrades ?? undefined,
);
const castingNumber = ref<string | undefined>(
  props.part?.casting_number ?? undefined,
);
const matchedHardware = ref<boolean | undefined>(
  props.part?.matched_hardware ?? undefined,
);
const totalFactoredFiredHours = ref<number | undefined>(
  props.part?.total_factored_fired_hours ?? undefined,
);
const totalFactoredFiredStarts = ref<number | undefined>(
  props.part?.total_factored_fired_starts ?? undefined,
);
const emergencyTrips = ref<number | undefined>(
  props.part?.emergency_trips ?? undefined,
);
const timesRefurbished = ref<number | undefined>(
  props.part?.times_refurbished ?? undefined,
);
const status = ref<"available" | "pending" | "sold" | "removed" | undefined>(
  props.part?.status ?? undefined,
);
const condition = ref<
  | "new"
  | "repairable"
  | "repaired"
  | "as removed"
  | "inspected"
  | "scrap"
  | undefined
>(props.part?.condition ?? undefined);
const partType = ref<PartType | null>(props.part?.part_type ?? null);
const dateListed = ref<string | undefined>(
  props.part?.date_listed ?? undefined,
);
const dateSold = ref<string | undefined>(props.part?.date_sold ?? undefined);
const tags = ref<string[]>(props.part?.tags || []);
const thumbnailUrl = ref<string | undefined>(
  props.part?.thumbnail_url ?? undefined,
);
const orgId = ref<string | null>(props.part?.org_id ?? null);

// Address field
const address = ref<Address | null>(props.part?.address ?? null);

// Computed properties
const isUpdate = computed(() => !!props.part);

// Photo file options for thumbnail selection
const photoFileOptions = computed(() => {
  const photoFiles = existingFiles.value.filter(
    (file) => file.category === "photo",
  );
  return photoFiles.map((file) => ({
    title: file.file_original_name,
    value: file.url || "", // Use the URL from the file object
    raw: {
      title: file.file_original_name,
      value: file.url || "",
    },
  }));
});

// Organizations (admin list — hidden when caller is not admin)
const organizationsQuery = useListOrganizations();

const showOrganizationSelect = computed(
  () =>
    !organizationsQuery.isError.value &&
    (organizationsQuery.isPending.value || organizationsQuery.isSuccess.value),
);

const organizationSelectItems = computed(() => {
  const data = organizationsQuery.data.value;
  if (!data?.organizations?.length) return [];
  const usersById = new Map(data.users.map((u) => [u.id, u]));
  return data.organizations.map((org) => {
    const user = usersById.get(org.creator_id);
    return {
      title: `${org.name} — ${formatCreatorLabel(user)}`,
      value: org.id,
    };
  });
});

const canAssignOrganization = computed(
  () => organizationsQuery.isSuccess.value,
);

// File handling
const existingFiles = ref<PartFile[]>([]);

// File query - only run if we have a part ID (update mode)
const partIdRef = ref(props.part?.id || "");
const creating = computed(() => !props.part?.id);
const filesQuery = useGetPartFiles({ partId: partIdRef });

const filesLoading = computed(() => filesQuery.isLoading.value);

// Watch for part prop changes to update partIdRef
watch(
  () => props.part?.id,
  (newPartId) => {
    partIdRef.value = newPartId || "";
  },
  { immediate: true },
);

watch(
  () => props.part?.org_id,
  (next) => {
    orgId.value = next ?? null;
  },
);

watch(
  () => props.part?.price_unit_amount,
  (cents) => {
    priceDollars.value =
      cents != null ? (cents / 100).toFixed(2) : "";
  },
);

// Watch for files data changes
watch(
  () => filesQuery.data.value,
  (newFiles) => {
    if (newFiles) {
      existingFiles.value = newFiles;
    }
  },
  { immediate: true },
);

// Mutations
const createMutation = useCreatePart();
const updateMutation = useUpdatePart();
const loading = computed(
  () => createMutation.isPending.value || updateMutation.isPending.value,
);

// TODO: Validate data with JSON schema.

// Emits
const emit = defineEmits<{
  success: [part: any];
  cancel: [];
}>();

// File event handlers
const onFileUploaded = (file: PartFile) => {
  existingFiles.value = [...existingFiles.value, file];

  // If this is a photo file and no thumbnail is currently selected, set it as thumbnail
  if (file.category === "photo" && !thumbnailUrl.value) {
    thumbnailUrl.value = file.url || "";
  }
};

const onFileDeleted = (file: PartFile) => {
  existingFiles.value = existingFiles.value.filter((f) => f.id !== file.id);

  // If the deleted file was the current thumbnail, clear the selection
  if (file.category === "photo" && thumbnailUrl.value === file.url) {
    thumbnailUrl.value = undefined;
  }
};

// Form submission
const onSubmit = async () => {
  if (!valid.value) return;

  try {
    const data: PartRequest = {
      manufacturer: manufacturer.value,
      frame: frame.value,
      part_number: partNumber.value,
      turbine_model: turbineModel.value,
      quantity: Number(quantity.value),
      description: description.value || undefined,
      serial_number: serialNumber.value || undefined,
      upgrades: upgradeDesignations.value || undefined,
      casting_number: castingNumber.value || undefined,
      matched_hardware: matchedHardware.value || false,
      total_factored_fired_hours: totalFactoredFiredHours.value || undefined,
      total_factored_fired_starts: totalFactoredFiredStarts.value || undefined,
      emergency_trips: emergencyTrips.value || undefined,
      times_refurbished: timesRefurbished.value || undefined,
      price_currency: "USD",
      price_unit_amount: priceDollarsToCents(priceDollars.value),
      status: status.value || undefined,
      condition: condition.value || undefined,
      part_type: partType.value ?? null,
      date_listed: dateListed.value || undefined,
      date_sold: dateSold.value || undefined,
      tags: tags.value.length > 0 ? tags.value : undefined,
      thumbnail_url: thumbnailUrl.value || undefined,
      address: address.value || undefined,
      ...(canAssignOrganization.value ? { org_id: orgId.value ?? null } : {}),
    };

    let result;
    if (isUpdate.value && props.part) {
      const updated = await updateMutation.mutateAsync({
        id: props.part.id,
        data,
      });
      result = updated.part;
    } else {
      const created = await createMutation.mutateAsync(data);
      result = created.part;
    }

    emit("success", result);
  } catch (error) {
    // Error handling is done by the mutation
    if (error instanceof TanstackError) {
      showError(getTanstackErrorMessage(error));
    } else {
      showError(error instanceof Error ? error.message : "Unknown error");
    }
  }
};

const onCancel = () => {
  emit("cancel");
};
</script>
